// Web Push state + opt-in/out actions for the live-event app.
//
// This module only owns the *background/OS* push channel (service worker +
// PushManager). In-app notifications, the bell and unread count live in the
// `notifications` module; this one is purely about enrolling the device so
// notifications also arrive when the tab is closed.

import {
  isPushSupported,
  enablePush,
  disablePush,
  syncPushIfEnabled
} from '@/helpers/push-notifications';

export const state = () => ({
  supported: isPushSupported(),
  // 'default' | 'granted' | 'denied' | 'unsupported'
  permission:
    typeof Notification !== 'undefined'
      ? Notification.permission
      : 'unsupported',
  subscribed: false
});

export const getters = {
  isSupported: (state) => state.supported,
  permission: (state) => state.permission,
  isSubscribed: (state) => state.subscribed,
  // Whether to surface an "Enable notifications" prompt in the UI.
  canPrompt: (state) =>
    state.supported && state.permission === 'default' && !state.subscribed
};

export const mutations = {
  setPermission(state, permission) {
    state.permission = permission;
  },
  setSubscribed(state, value) {
    state.subscribed = !!value;
  }
};

export const actions = {
  // Triggered from a user gesture (e.g. an "Enable notifications" button).
  async enable({ commit, rootGetters }) {
    const eventId = rootGetters['CurrentEvent/getId'];
    const ok = await enablePush(eventId);
    commit(
      'setPermission',
      typeof Notification !== 'undefined'
        ? Notification.permission
        : 'unsupported'
    );
    commit('setSubscribed', ok);
    return ok;
  },
  async disable({ commit }) {
    await disablePush();
    commit('setSubscribed', false);
  },
  // Silent re-sync — ensures a backend subscription exists when permission is
  // already granted. Never prompts. Returns true only when a subscription was
  // saved server-side, so callers can retry.
  async sync({ commit, rootGetters }) {
    const eventId = rootGetters['CurrentEvent/getId'];
    const subscribed = await syncPushIfEnabled(eventId);
    if (typeof Notification !== 'undefined') {
      commit('setPermission', Notification.permission);
      commit('setSubscribed', subscribed);
    }
    return subscribed;
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
